import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { FinancialDocument } from "../../common/types/financialDocument";
import { IntegrationTransaction } from "../../common/types/integrationTransaction";
import { FormRowRaw } from "../forms/FormRowRaw";
import { MonetaryInput } from "../forms/MonetaryInput";
import { DatePickerInput } from "../forms/dates/DatePickerInput/DatePickerInput";
import {
    fromIntToMonetary,
    fromMonetaryToInt,
} from "../../common/helpers/monetary";
import { currencyFormatter } from "../../common/helpers/currency";
import { fullDateFormat } from "../../common/helpers/date";
import { useUpdateFinancialDocumentMetadataMutation } from "../../api/financialDocument.api";
import styles from "./ImporterPage.module.scss";
import { ImporterContext } from "./importer.context";

interface Props {
    documentRowId: string;
    financialDocument: FinancialDocument;
    transactions: IntegrationTransaction[];
}

export const ImporterBalanceSummary: React.FC<Props> = ({
    documentRowId,
    financialDocument,
    transactions,
}) => {
    const { updateDocumentRow } = useContext(ImporterContext);

    const [openingBalance, setOpeningBalance] = useState(
        financialDocument.openingBalance ?? undefined,
    );
    const [closingBalance, setClosingBalance] = useState(
        financialDocument.closingBalance ?? undefined,
    );
    const [openingDate, setOpeningDate] = useState(
        financialDocument.openingDate ?? undefined,
    );
    const [closingDate, setClosingDate] = useState(
        financialDocument.closingDate ?? undefined,
    );

    useEffect(() => {
        setOpeningBalance(financialDocument.openingBalance ?? undefined);
        setClosingBalance(financialDocument.closingBalance ?? undefined);
        setOpeningDate(financialDocument.openingDate ?? undefined);
        setClosingDate(financialDocument.closingDate ?? undefined);
    }, [financialDocument]);

    const container = useRef<HTMLDivElement>(null);

    const moneyIn = useMemo(
        () =>
            fromIntToMonetary(
                transactions.reduce(
                    (acc, t) =>
                        acc + (t.amount > 0 ? fromMonetaryToInt(t.amount) : 0),
                    0,
                ),
            ),
        [transactions],
    );
    const moneyOut = useMemo(
        () =>
            Math.abs(
                fromIntToMonetary(
                    transactions.reduce(
                        (acc, t) =>
                            acc +
                            (t.amount < 0 ? fromMonetaryToInt(t.amount) : 0),
                        0,
                    ),
                ),
            ),
        [transactions],
    );
    const moneyInString = `+ ${currencyFormatter.format(moneyIn)}`;
    const moneyOutString = `- ${currencyFormatter.format(moneyOut)}`;

    const updateDocumentMetadata = useUpdateFinancialDocumentMetadataMutation(
        financialDocument,
        (updated) => {
            updateDocumentRow(documentRowId, {
                financialDocument: updated,
            });
        },
    );

    const updateDate = (
        date: string | undefined,
        field: "openingDate" | "closingDate",
    ) => {
        if (field === "openingDate") {
            setOpeningDate(date);
            updateDocumentMetadata.mutate({
                openingDate: date,
            });
        } else {
            setClosingDate(date);
            updateDocumentMetadata.mutate({
                closingDate: date,
            });
        }
    };

    const saveBalance = (field: "openingBalance" | "closingBalance") => {
        if (
            field === "openingBalance" &&
            openingBalance !== financialDocument.openingBalance
        ) {
            updateDocumentMetadata.mutate({
                openingBalance,
            });
        }
        if (
            field === "closingBalance" &&
            closingBalance !== financialDocument.closingBalance
        ) {
            updateDocumentMetadata.mutate({
                closingBalance,
            });
        }
    };

    return (
        <section className={styles.balanceSummary} ref={container}>
            <FormRowRaw
                label="Opening balance"
                value={openingBalance}
                field={
                    <div className="input-group">
                        <MonetaryInput
                            value={openingBalance}
                            onChange={setOpeningBalance}
                            size="xs"
                            onBlur={() => saveBalance("openingBalance")}
                        />
                        <DatePickerInput
                            value={openingDate ?? ""}
                            onChange={(date) => updateDate(date, "openingDate")}
                            dateFormat={fullDateFormat}
                            inputSize="xs"
                            name="openingDate"
                            popoverContainer={container.current ?? undefined}
                        />
                    </div>
                }
            />

            <div className="input-group">
                <FormRowRaw
                    label="$ in"
                    value={moneyInString}
                    size="xs"
                    disabled
                />
                <FormRowRaw
                    label="$ out"
                    value={moneyOutString}
                    size="xs"
                    disabled
                />
            </div>

            <FormRowRaw
                label="Closing balance"
                value={closingBalance}
                field={
                    <div className="input-group">
                        <MonetaryInput
                            value={closingBalance}
                            onChange={setClosingBalance}
                            size="xs"
                            onBlur={() => saveBalance("closingBalance")}
                        />
                        <DatePickerInput
                            value={closingDate ?? ""}
                            onChange={(date) => updateDate(date, "closingDate")}
                            dateFormat={fullDateFormat}
                            inputSize="xs"
                            name="closingDate"
                            popoverContainer={container.current ?? undefined}
                        />
                    </div>
                }
            />
        </section>
    );
};
